import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { annualReconciliationAPI } from '../../services/api';
import ErrorModal from '../Common/ErrorModal';
import './AnnualReconciliationCreate.css';

const AnnualReconciliationView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [reconciliation, setReconciliation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadAnnualReconciliation();
  }, [id]);

  const loadAnnualReconciliation = async () => {
    try {
      setLoading(true);
      const response = await annualReconciliationAPI.getById(id);
      const data = response.data;
      setReconciliation({
        ...data,
        documentDate: data.documentDate ? new Date(data.documentDate).toISOString().split('T')[0] : '',
        periodFrom: data.periodFrom ? new Date(data.periodFrom).toISOString().split('T')[0] : '',
        periodUntil: data.periodUntil ? new Date(data.periodUntil).toISOString().split('T')[0] : '',
        isActive: data.isActive ? 'true' : 'false',
      });
    } catch (err) {
      setErrorMessage(t('annualReconciliation.view.messages.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!reconciliation) {
    return (
      <div className="page-container">
        <div className="alert alert-danger">{t('annualReconciliation.list.messages.noData')}</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header-actions">
        <button className="btn-back" onClick={() => navigate('/annual-reconciliation')}>
          <i className="fas fa-arrow-left"></i> {t('annualReconciliation.view.backToList')}
        </button>
        <button className="btn-edit" onClick={() => navigate(`/annual-reconciliation/edit/${id}`)}>
          <i className="fas fa-edit"></i> {t('annualReconciliation.view.edit')}
        </button>
      </div>

      <div className="form-container">
        {/* Document Information */}
        <h3 className="form-section-title">{t('annualReconciliation.view.sections.documentInformation')}</h3>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-calendar"></i> {t('annualReconciliation.view.fields.documentDate')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-calendar input-icon"></i>
              <input
                type="date"
                className="form-input"
                value={reconciliation.documentDate}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-map-marker-alt"></i> {t('annualReconciliation.view.fields.issuedIn')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-map-marker-alt input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={reconciliation.issuedIn || '-'}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-check-circle"></i> {t('annualReconciliation.view.fields.isActive')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-check-circle input-icon"></i>
              <select
                className="form-input"
                value={reconciliation.isActive}
                disabled
              >
                <option value="true">{t('common.yes')}</option>
                <option value="false">{t('common.no')}</option>
              </select>
            </div>
          </div>
        </div>

        {/* First Party */}
        <h3 className="form-section-title">{t('annualReconciliation.view.sections.firstParty')}</h3>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-user"></i> {t('annualReconciliation.view.fields.firstPartyName')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-user input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={reconciliation.firstPartyName}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-id-card"></i> {t('annualReconciliation.view.fields.firstPartyIdNumber')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-id-card input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={reconciliation.firstPartyIdNumber}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-flag"></i> {t('annualReconciliation.view.fields.firstPartyNationality')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-flag input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={reconciliation.firstPartyNationality || '-'}
                readOnly
                disabled
              />
            </div>
          </div>
        </div>

        {/* Second Party */}
        <h3 className="form-section-title">{t('annualReconciliation.view.sections.secondParty')}</h3>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-user"></i> {t('annualReconciliation.view.fields.secondPartyName')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-user input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={reconciliation.secondPartyName || '-'}
                readOnly
                disabled
              />
            </div>
          </div>
        </div>

        {/* Property Information */}
        <h3 className="form-section-title">{t('annualReconciliation.view.sections.propertyInformation')}</h3>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-building"></i> {t('annualReconciliation.view.fields.propertyType')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-building input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={reconciliation.propertyType || '-'}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-map-marked-alt"></i> {t('annualReconciliation.view.fields.propertyDistrict')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-map-marked-alt input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={reconciliation.propertyDistrict || '-'}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-hashtag"></i> {t('annualReconciliation.view.fields.plotNumber')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-hashtag input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={reconciliation.plotNumber || '-'}
                readOnly
                disabled
              />
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-calendar"></i> {t('annualReconciliation.view.fields.periodFrom')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-calendar input-icon"></i>
              <input
                type="date"
                className="form-input"
                value={reconciliation.periodFrom}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-calendar"></i> {t('annualReconciliation.view.fields.periodUntil')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-calendar input-icon"></i>
              <input
                type="date"
                className="form-input"
                value={reconciliation.periodUntil}
                readOnly
                disabled
              />
            </div>
          </div>
        </div>
      </div>

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </div>
  );
};

export default AnnualReconciliationView;
